import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { useComment } from "../../../contexts/CommentContext";
import { Button } from "../../../design/button";
import SlateEditor, { SlateEditorRef } from "../../SlateTextArea/SlateTextArea";
import { Descendant } from "slate";
import { useMatter } from "../../../contexts/MatterContext";
import ReplyListItem from "./ReplyListItem";
import { BadgeButton } from "../../../design/badge";
import { Comment } from "../../../models/Comment";

interface Props {
  commentId: Comment["_id"];
}

const CreateReplyButton: React.FC<Props> = ({ commentId }) => {
  const { createReply } = useComment();

  const editorRef = useRef<SlateEditorRef | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [comment, setComment] = useState<Descendant[] | null>(null);
  const [replies, setReplies] = useState<Comment[]>([]);

  const handleSubmit = useCallback(async () => {
    if (!comment) return;

    setIsSaving(true);
    try {
      await createReply(commentId, {
        parentType: "Comment",
        parent: commentId,
        content: JSON.stringify(comment),
        comments: [],
      });

      setComment(null);
      editorRef.current?.resetEditor();
      setIsOpen(false);
    } catch (error) {
      console.error("Error adding reply:", error);
    } finally {
      setIsSaving(false);
    }
  }, [comment, commentId, createReply]);

  return (
    <>
      <BadgeButton onClick={() => setIsOpen(true)} aria-label="Reply">
        Reply
      </BadgeButton>

      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle>Reply</DialogTitle>
        <DialogBody>
          <div>
            {replies.map((reply) => (
              <ReplyListItem key={reply._id} reply={reply} />
            ))}
          </div>
        </DialogBody>
        <DialogActions>
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              color: "red",
            }}
          >
            <SlateEditor
              id={commentId}
              placeholder="Write a reply..."
              ref={editorRef}
              initialValue={comment ?? undefined}
              onChange={setComment}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <Button color="zinc" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                color="green"
                onClick={handleSubmit}
                disabled={isSaving || !comment}
              >
                {isSaving ? "Posting..." : "Post"}
              </Button>
            </div>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateReplyButton;
